import { useCallback } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { addItemToCache, removeItemFromCache } from '@ts-core/core/utils/apolloUtils';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';

import { ProfessorActivity, ActivityInput } from '../types/index';

import { getActivitiesQuery, GetActivitiesQueryVariables, GetActivitiesQueryResult } from '../graphql/queries/getActivitiesQuery';

import { createActivityMutation, CreateActivityMutationResult, CreateActivityMutationVariables } from '../graphql/mutations/createActivityMutation';
import { updateActivityMutation, UpdateActivityMutationResult, UpdateActivityMutationVariables } from '../graphql/mutations/updateActivityMutation';
import { deleteActivityMutation, DeleteActivityMutationResult, DeleteActivityMutationVariables } from '../graphql/mutations/deleteActivityMutation';
import { toggleActivityMutation, ToggleActivityMutationResult, ToggleActivityMutationVariables } from '../graphql/mutations/toggleActivityMutation';

interface ActivityActionsHookInput {
  courseId: string;
}

interface ActivityActionsHookPayload {
  onCreateActivity: (input: ActivityInput, onCreated?: () => void) => void;
  createActivityLoading: boolean;
  createActivityError?: string;

  onUpdateActivity: (activityId: string, input: ActivityInput, onUpdated?: () => void) => void;
  updateActivityLoading: boolean;
  updateActivityError?: string;

  onToggleActivity: (activity: ProfessorActivity) => void;

  onDeleteActivity: (activity: ProfessorActivity) => void;
}

export default function useActivityActions({ courseId }: ActivityActionsHookInput): ActivityActionsHookPayload {
  const { showErrorNotification, showSuccessNotification } = useSnackbarNotifications();

  /**
   * Create Activity
   */
  const [createActivity, { loading: createActivityLoading, error: createActivityError }] = useMutation<
    CreateActivityMutationResult,
    CreateActivityMutationVariables
  >(createActivityMutation, {
    onError: () => {},
    update: addItemToCache<CreateActivityMutationResult, GetActivitiesQueryResult, GetActivitiesQueryVariables>({
      query: getActivitiesQuery,
      queryName: 'activities',
      queryVariables: { courseId },
      mutationName: 'activity',
    }),
  });
  const onCreateActivity = useCallback(
    (input: ActivityInput, onCreated?: () => void) => {
      createActivity({
        variables: {
          courseId,
          input,
        },
      }).then(onCreated);
    },
    [courseId, createActivity]
  );

  /**
   * Update Activity
   */
  const [updateActivity, { loading: updateActivityLoading, error: updateActivityError }] = useMutation<
    UpdateActivityMutationResult,
    UpdateActivityMutationVariables
  >(updateActivityMutation, {
    onCompleted: () => showSuccessNotification(),
    onError: showErrorNotification,
  });
  const onUpdateActivity = useCallback(
    (activityId: string, input: ActivityInput, onUpdated?: () => void) => {
      updateActivity({
        variables: {
          courseId,
          activityId,
          input,
        },
      }).then(onUpdated);
    },
    [courseId, updateActivity]
  );

  /**
   * Toggle Activity
   */
  const [toggleActivity] = useMutation<ToggleActivityMutationResult, ToggleActivityMutationVariables>(toggleActivityMutation, {
    onError: showErrorNotification,
  });
  const onToggleActivity = useCallback(
    (activity: ProfessorActivity) => {
      const updatedActivity = {
        ...activity,
        enabled: !activity.enabled,
      };

      toggleActivity({
        variables: {
          courseId,
          activityId: activity.id,
          status: activity.enabled ? 'disable' : 'enable',
        },
        optimisticResponse: {
          activity: updatedActivity,
        },
      });
    },
    [courseId, toggleActivity]
  );

  /**
   * Delete Activity
   */
  const [deleteActivity] = useMutation<DeleteActivityMutationResult, DeleteActivityMutationVariables>(deleteActivityMutation, {
    onError: showErrorNotification,
    update: removeItemFromCache<DeleteActivityMutationResult, GetActivitiesQueryResult, GetActivitiesQueryVariables>({
      query: getActivitiesQuery,
      queryVariables: { courseId },
      queryName: 'activities',
      mutationName: 'activity',
      idAccessor: 'id',
    }),
  });
  const onDeleteActivity = useCallback(
    (activity: ProfessorActivity) => {
      deleteActivity({
        variables: {
          courseId,
          activityId: activity.id,
        },
        optimisticResponse: { activity },
      });
    },
    [courseId, deleteActivity]
  );

  return {
    onCreateActivity,
    createActivityLoading,
    createActivityError: getErrorMessage({ error: createActivityError }),

    onUpdateActivity,
    updateActivityLoading,
    updateActivityError: getErrorMessage({ error: updateActivityError }),

    onToggleActivity,

    onDeleteActivity,
  };
}
